import { Colours, StepItem } from '@ecdlink/ui';
import { Maybe, TraineeOnBoardTimeline, Visit } from '@ecdlink/graphql';

export const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const filterVisit = (visit: Maybe<Visit>) =>
  !visit?.attended && typeof visit?.visitType?.order !== 'undefined';

export const sortVisit = (visitA?: Maybe<Visit>, visitB?: Maybe<Visit>) => {
  const orderA = Number(visitA?.visitType?.order) || 0;
  const orderB = Number(visitB?.visitType?.order) || 0;
  return orderA - orderB;
};

export const getStepType = (
  color?: Maybe<string>
): { type: StepItem['type']; color?: Colours } => {
  if (!color) return { type: 'todo' };
  switch (color.toLowerCase()) {
    case 'success':
      return { type: 'completed' };
    case 'consolidation meeting scheduled':
      return { type: 'inProgress' };
    case 'warning':
      return { type: 'inProgress', color: 'alertDark' };
    case 'error':
      return { type: 'inProgress', color: 'alertDark' };
    default:
      return { type: 'todo' };
  }
};

export const getStepDate = (date?: string, visitEventId?: string) =>
  !!date
    ? `${!!visitEventId ? 'Scheduled' : 'By'} ${new Date(
        date
      ).toLocaleDateString('en-ZA', dateOptions)}`
    : '';

const statusTracker: Record<string, { isActive: boolean; type?: string }> = {};

const trackStatus = (status: string, type?: string) => {
  if (status) {
    statusTracker[status] = {
      isActive: true,
      type: type ?? statusTracker[status]?.type, // Preserve existing type if not provided
    };
  }
};

// Because this is a functional componenet
// The data must be manually reset when making new instances
export const resetStatusTracker = () => {
  Object.keys(statusTracker).forEach((key) => delete statusTracker[key]);
};

export const setStep = (
  starterLicenceState?: Maybe<string>,
  status?: Maybe<string>,
  date?: string,
  color?: Maybe<string>,
  onView?: (text: string) => void,
  nextStep?: string,
  visitEventId?: string,
  previousStep?: string
): StepItem<{
  date?: Date | null;
}> => {
  if (status) {
    trackStatus(status, getStepType(color).type);
  }

  const lincenceReceveid = 'Starter Licence received';
  const smartSpaceLincenceReceveid = 'SmartSpace Licence received';
  const register3Children = 'Register 3 children';
  const register3Children2 = '3 or more children registered';
  const communitySupport = 'Get community support';
  const communitySupport2 = 'Community support gained';
  const franchisorAgreement = 'Sign franchisee agreement';
  const franchisorAgreement2 = 'Franchisee agreement signed';
  const smartSpaceVisitFromCoach = 'SmartSpace visit from coach';
  const consolidationMeetingScheduled = 'Consolidation meeting scheduled';
  const consolidationMeetingOverdue = 'Consolidation meeting overdue';
  const consolidationMeetingAttended = 'Consolidation meeting attended';
  const licenceNotAwarded = 'SmartSpace Licence not awarded';
  const licenceAwarded = 'SmartSpace Licence received';
  const smartSpaceChecklist = 'Fill in the SmartSpace checklist';
  const signStartUpSupport = 'Sign start-up support agreement';
  const signStartUpSupport2 = 'Start-up support agreement signed';

  const franchiseeAgreementSignable =
    !!statusTracker[franchisorAgreement]?.isActive &&
    !!statusTracker[smartSpaceVisitFromCoach]?.isActive &&
    statusTracker[smartSpaceVisitFromCoach]?.type === 'completed';

  const stepCompleted =
    color?.toLowerCase() === 'success' &&
    status !== lincenceReceveid &&
    status !== smartSpaceLincenceReceveid;

  const notShowButtonRules =
    status === licenceNotAwarded ||
    status === licenceAwarded ||
    status === consolidationMeetingScheduled ||
    status === consolidationMeetingOverdue ||
    status === consolidationMeetingAttended ||
    (status === signStartUpSupport &&
      statusTracker[franchisorAgreement2]?.type === 'completed' &&
      statusTracker[register3Children2]?.type === 'completed') ||
    (status === communitySupport &&
      !!(
        statusTracker?.[consolidationMeetingAttended]?.type === 'completed'
      )) ||
    (stepCompleted &&
      status !== register3Children2 &&
      status !== communitySupport2 &&
      status !== franchisorAgreement2 &&
      status !== consolidationMeetingAttended &&
      status !== signStartUpSupport2 &&
      status !== smartSpaceVisitFromCoach);

  const addChildrenStep = !!(
    statusTracker?.[communitySupport2]?.isActive &&
    statusTracker?.[register3Children]?.isActive
  );

  const calendarViewRules =
    stepCompleted ||
    status === franchisorAgreement ||
    status === lincenceReceveid ||
    status === licenceNotAwarded ||
    status === licenceAwarded ||
    status === smartSpaceChecklist ||
    status === signStartUpSupport ||
    status === consolidationMeetingScheduled ||
    status === consolidationMeetingOverdue ||
    status === consolidationMeetingAttended ||
    status === communitySupport2;

  if (!!status) {
    return {
      title: status,
      color: status === licenceNotAwarded && 'errorDark',
      subTitle: getStepDate(date, visitEventId),
      inProgressStepIcon:
        (status === licenceNotAwarded && 'XIcon') ||
        (status === 'consolidationMeetingScheduled' && 'CalendarIcon') ||
        (status === consolidationMeetingAttended && 'CalendarIcon') ||
        status === communitySupport2 ||
        (status === smartSpaceVisitFromCoach &&
          !!visitEventId &&
          'CalendarIcon') ||
        ((color === 'Warning' || color === 'Error') && 'ExclamationCircleIcon'),
      subTitleColor:
        (status === licenceNotAwarded && 'errorMain') ||
        (date &&
        new Date(date).getTime() < new Date().getTime() &&
        color?.toLowerCase() !== 'success'
          ? 'alertMain'
          : getStepType(color)?.color || ''),
      type:
        status === consolidationMeetingAttended
          ? 'completed'
          : getStepType(color).type,
      extraData: { date: date ? new Date(date) : null },
      showActionButton:
        starterLicenceState === 'Starter Licence not received'
          ? false
          : notShowButtonRules ||
            (addChildrenStep && status === register3Children) ||
            (nextStep === status && status !== communitySupport) ||
            (status === smartSpaceVisitFromCoach && previousStep) ||
            (franchiseeAgreementSignable && status === franchisorAgreement)
          ? true
          : false,

      actionButtonText:
        addChildrenStep && status === register3Children
          ? 'Add child'
          : ((calendarViewRules && status !== smartSpaceVisitFromCoach) ||
              status === communitySupport) &&
            !previousStep
          ? 'View'
          : !!visitEventId
          ? 'Start'
          : 'Schedule',
      actionButtonTextColor:
        ((calendarViewRules && status !== smartSpaceVisitFromCoach) ||
          status === communitySupport) &&
        !previousStep
          ? 'secondary'
          : !!visitEventId ||
            (status === smartSpaceVisitFromCoach && previousStep)
          ? 'white'
          : 'primary',
      actionButtonColor:
        ((calendarViewRules && status !== smartSpaceVisitFromCoach) ||
          status === communitySupport) &&
        !previousStep
          ? 'secondaryAccent2'
          : 'primary',
      actionButtonIcon:
        status === register3Children
          ? 'UserAddIcon'
          : addChildrenStep && status === register3Children
          ? 'PlusIcon'
          : ((calendarViewRules && status !== smartSpaceVisitFromCoach) ||
              status === communitySupport) &&
            !previousStep
          ? ''
          : !!visitEventId
          ? 'ArrowCircleRightIcon'
          : 'CalendarIcon',
      actionButtonOnClick: onView,
      actionButtonType:
        calendarViewRules ||
        !!visitEventId ||
        (status === smartSpaceVisitFromCoach && previousStep) ||
        status === communitySupport
          ? 'filled'
          : 'outlined',
      actionButtonIconStartPosition:
        stepCompleted || !!visitEventId ? false : true,
      actionButtonClassName: stepCompleted
        ? ''
        : 'w-full whitespace-nowrap p-2 mt-2',
    } as StepItem<{ date?: Date | null }>;
  }

  return {
    title: status,
    subTitle: getStepDate(date),
    inProgressStepIcon:
      (color === 'Warning' || color === 'Error') && 'ExclamationCircleIcon',
    subTitleColor: getStepType(color)?.color || '',
    completedStepIcon: status === communitySupport2 && 'ThumbUpIcon',
    type: getStepType(color).type,
    extraData: { date: date ? new Date(date) : null },
  } as StepItem<{ date?: Date | null }>;
};

export const timelineSteps = (
  timeline: TraineeOnBoardTimeline,
  onView: (notificationStep: string, options?: any) => void,
  isLoading: boolean,
  isOnline: boolean,
  visits?: Maybe<Visit>[],
  nextStep?: string,
  isOnStipend?: boolean
): StepItem<{
  date?: Date;
}>[] => {
  resetStatusTracker();

  const steps: (StepItem<{ date?: Date }> | {})[] = [];

  steps.push(
    setStep(
      timeline?.starterLicenseStatus,
      timeline?.starterLicenseStatus || 'Starter Licence',
      timeline?.starterLicenseDate,
      timeline?.starterLicenseColor,
      () => onView('Starter Licence'),
      nextStep
    )
  );
  if (
    timeline?.consolidationDeadlineDate < new Date() &&
    timeline?.consolidationMeetingDate == null
  ) {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        'Consolidation meeting overdue',
        timeline?.consolidationDeadlineDate,
        'error',
        () => onView('Consolidation meeting overdue'),
        nextStep
      )
    );
  } else {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        timeline?.consolidationMeetingStatus ||
          'Consolidation meeting scheduled',
        timeline?.consolidationMeetingDate ||
          timeline?.consolidationDeadlineDate,
        timeline?.consolidationMeetingColor,
        () => onView('Consolidation meeting scheduled'),
        nextStep
      )
    );
  }
  steps.push(
    setStep(
      timeline?.starterLicenseStatus,
      timeline?.smartSpaceChecklistStatus || 'Fill in the SmartSpace checklist',
      timeline?.smartSpaceChecklistDate ||
        timeline?.smartSpaceChecklistDeadlineDate,
      timeline?.smartSpaceChecklistColor,
      () => onView('Fill in the SmartSpace checklist'),
      nextStep
    )
  );
  steps.push(
    setStep(
      timeline?.starterLicenseStatus,
      timeline?.communitySupportStatus || 'Get community support',
      timeline?.communitySupportDate || timeline?.communitySupportDeadlineDate,
      timeline?.communitySupportColor,
      () => onView('Get community support'),
      nextStep
    )
  );
  steps.push(
    setStep(
      timeline?.starterLicenseStatus,
      timeline?.threeChildrenRegisteredStatus || 'Register 3 children',
      timeline?.threeChildrenRegisteredDate ||
        timeline?.threeChildrenRegisteredDeadlineDate,
      timeline?.threeChildrenRegisteredColor,
      () => onView('Register 3 children'),
      nextStep
    )
  );

  if (
    timeline?.smartSpaceLicenseNotAwardedDate &&
    timeline?.smartSpaceLicenseStatus === 'SmartSpace Licence not received'
  ) {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        'SmartSpace Licence not awarded',
        timeline?.smartSpaceLicenseNotAwardedDate,
        'error',
        () => onView('SmartSpace Licence not awarded'),
        nextStep
      )
    );
  }

  if (
    timeline?.smartSpaceLicenseStatus === 'SmartSpace Licence received' &&
    timeline?.smartSpaceLicenseDate
  ) {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        'SmartSpace Licence received',
        timeline?.smartSpaceLicenseDate,
        'success',
        () => onView('SmartSpace Licence received'),
        nextStep
      )
    );
  }

  if (
    timeline?.smartSpaceLicenseStatus !== 'SmartSpace Licence received' &&
    (!timeline?.smartSpaceLicenseDate ||
      timeline?.smartSpaceLicenseNotAwardedDate)
  ) {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        timeline?.sSCoachVisitStatus || 'SmartSpace visit from coach',
        timeline?.sSCoachVisitDate || timeline?.sSCoachVisitDeadlineDate,
        timeline?.sSCoachVisitColor,
        () =>
          onView('SmartSpace visit from coach', {
            visitEventId: (timeline.sSCoachVisitEventId as string) || '',
            plannedVisitDate: (timeline.sSCoachVisitDate as string) || '',
            visitId: (timeline.sSCoachVisitId as string) || '',
          }),
        nextStep,
        timeline?.sSCoachVisitEventId,
        timeline?.smartSpaceLicenseNotAwardedDate ||
          (timeline?.threeChildrenRegisteredColor === 'Success' &&
            !timeline?.sSCoachVisitDone)
      )
    );
  }
  steps.push(
    setStep(
      timeline?.starterLicenseStatus,
      timeline?.signFranchiseeAgreementStatus || 'Sign franchisee agreement',
      timeline?.signFranchiseeAgreementDate ||
        timeline?.signFranchiseeAgreementDeadlineDate,
      timeline?.signFranchiseeAgreementColor,
      () => onView('Sign franchisee agreement'),
      nextStep
    )
  );
  if (isOnStipend) {
    steps.push(
      setStep(
        timeline?.starterLicenseStatus,
        timeline?.signStartUpSupportAgreementStatus || 'Sign stipend agreement',
        timeline?.signStartUpSupportAgreementDate ||
          timeline?.signStartUpSupportAgreementDeadlineDate,
        timeline?.signStartUpSupportAgreementColor,
        () => onView('Sign start-up support agreement'),
        nextStep
      )
    );
  }

  return steps as StepItem<{ date: Date }>[];
};
